import { API_URL } from "env";
import { ProductsResponse } from "./ProductType";

export interface ProductListParams {
    sort?: string[];
    product_category_id?: number | string;
    slug_category?: string;
    id_group?: number | string;
    slug_group?: string;
    id_manufacturer?: number | string;
    slug_manufacturer?: string;
    is_stock?: number | string;
    star?: number | string;
    is_highlight?: number | string;
    list_id?: string;
    language?: string;
    name?: string;
    page?: number | string;
    limit?: number | string;
}

/**
 * Fetches a list of products from the API with support for various filtering and sorting parameters.
 *
 * @param params - Optional parameters to filter the product list:
 *   - `sort`: An array of sort strings (e.g., ['price:asc']).
 *   - `product_category_id`: The ID of the product category to filter by.
 *   - `slug_category`: The slug representation of the category.
 *   - `is_stock`: Filter by stock availability (0 or 1).
 *   - `star`: Filter by product rating.
 *   - `is_highlight`: Filter by highlighted status.
 *   - `list_id`: A string of specific IDs to retrieve.
 *   - `language`: The language code for results.
 *   - `category_slug`: Alternative category slug parameter.
 * 
 * @returns A promise that resolves to a `ProductsResponse` containing product items,
 * pagination data, and status. Returns a default empty structure if the request fails.
 * 
 * @throws Will not throw an error but logs it to the console and returns a fallback object on failure.
 */
async function fetchProductList(params?: ProductListParams): Promise<ProductsResponse> {
    const query = new URLSearchParams();
    if (params) {
        if (params.sort) params.sort.forEach(s => query.append('sort[]', s));
        if (params.product_category_id) query.append('product_category_id', String(params.product_category_id));
        if (params.slug_category) query.append('slug_category', params.slug_category);
        if (params.is_stock !== undefined) query.append('is_stock', String(params.is_stock));
        if (params.star) query.append('star', String(params.star));
        if (params.is_highlight !== undefined) query.append('is_highlight', String(params.is_highlight));
        if (params.list_id) query.append('list_id', params.list_id);
        if (params.language) query.append('language', params.language);
        if (params.page) query.append('page', String(params.page));
        if (params.name) query.append('name', params.name);
        if (params.limit) query.append('limit', String(params.limit));
        if (params.id_group) query.append('product_group_id', String(params.id_group));
        if (params.id_manufacturer) query.append('manufacturer_id', String(params.id_manufacturer));
    }

    const url = `${API_URL.replace(/\/$/, '')}/api/v1/fe/products${query.toString() ? `?${query.toString()}` : ''}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to fetch product list:', error);
        return { status: false, data: { total: 0, current_page: 0, items: [] }};
    }
}

async function fetchLastedProducts(params?: ProductListParams): Promise<ProductsResponse> {
    try {
        const query = new URLSearchParams();
        if (params) {
            if (params.language) query.append('language', params.language);
            if (params.limit) query.append('limit', String(params.limit));
            if (params.page) query.append('page', String(params.page));
            if (params.is_stock !== undefined) query.append('is_stock', String(params.is_stock));
            if (params.star) query.append('star', String(params.star));
            if (params.is_highlight !== undefined) query.append('is_highlight', String(params.is_highlight));
            if (params.name) query.append('name', params.name);
            if (params.product_category_id) query.append('product_category_id', String(params.product_category_id));
            if (params.slug_category) query.append('slug_category', params.slug_category);
            if (params.id_group) query.append('id_group', String(params.id_group));
            if (params.id_manufacturer) query.append('id_manufacturer', String(params.id_manufacturer));
            if (params.list_id) query.append('list_id', params.list_id);
        }
        query.append('sort[]', 'created_at,desc');

        const url = `${API_URL.replace(/\/$/, '')}/api/v1/fe/products${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;

    } catch (error) {
        console.error('Failed to fetch latest products:', error);
        return { status: false, data: { total: 0, current_page: 0, items: [] }};
    }
}


export {
    fetchProductList,
    fetchLastedProducts
}